/* eslint-disable  */
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  AlertAdapter,
  CommonDropdown,
  CommonInput,
  FormErrorWrap,
  ICommonDropdownOption,
  RegularButton,
} from 'pg-ui-library';
import { AxiosError } from 'axios';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import * as sheetLoader from 'src/utils/spreadsheetLoader';
import { FORM_TYPES } from 'src/interfaces';

import YearEndFormsService from 'src/services/year-end-forms.service';
import { YearEndForm } from './styles';

const SUPPORTED_FORMATS = ['xlsx', 'xls'];

const LoadingUpdateComponent: React.FC<{
  onSubmit: Function;
}> = ({ onSubmit }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const fetchProgress = async () => {
      const response = await YearEndFormsService.getYearEndFormProgress();
      setProgress(response.data as unknown as 0);
      if (response.data === 100) {
        AlertAdapter('Data Uploaded!', 'success');
        onSubmit();
      }
    };
    let percenntageInterval = setInterval(fetchProgress, 2500);
    return () => {
      clearInterval(percenntageInterval);
    };
  }, []);
  return (
    <div style={{ width: '100%', height: '30px' }}>
      <h4 style={{ textAlign: 'center', marginBottom: '1%' }}>{progress.toFixed(0)}%</h4>
      <div style={{ background: 'black', height: '10px', width: `${progress.toFixed(0)}%` }}></div>
    </div>
  );
};

export const FormUpload: React.FC<{
  onSubmit: Function;
}> = ({ onSubmit }) => {
  const year = new Date().getFullYear();
  const [formType, setFormType] = useState('W2');
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const { config } = useTypedSelector(state => state.config.data);
  const [dropdownOptions, setDropdownOptions] = useState<ICommonDropdownOption[]>([
    {
      key: 'W2',
      label: 'W2 Form',
      value: FORM_TYPES.W2,
    },
    {
      key: '1095-C',
      label: '1095-C Form',
      value: FORM_TYPES.FORM1095C,
    },
  ]);

  const mutation = useMutation((variables: FormData) => YearEndFormsService.patchYearEndForm(variables), {
    onSuccess: () => {
      AlertAdapter('Data received successfully, upload in progress...', 'success');
    },
    onError: (error: AxiosError) => {
      setIsUploading(false);
      AlertAdapter(error.response?.data.message, 'error');
    },
  });
  const validationSchema = yup.object().shape({
    companyId: yup.string().required(t('global.form.required')),
    year: yup
      .string()
      .matches(/^(19[5-9]\d|20[0-4]\d|2999)$/, t('global.form.invalidYear'))
      .test('is-future-date', t('global.form.invalidYear'), value => (value as unknown as Number) <= year)
      .required(t('global.form.required')),
    file: yup
      .mixed()
      .required(t('global.form.fileRequired'))
      .test(
        'fileFormat',
        t('global.form.unsupportedFormat'),
        value => value && SUPPORTED_FORMATS.includes(value.name.split('.').pop())
      ),
  });
  const formik = useFormik({
    initialValues: {
      companyId: config?.selectedCompanyId || '',
      year: '2022',
      file: undefined,
    },
    validationSchema,
    onSubmit: async values => {
      if (values.file) {
        const formData = new FormData();
        formData.append('file', values.file);
        formData.append('companyId', values.companyId);
        formData.append('year', values.year);
        formData.append('formType', formType);
        setIsUploading(true);
        if (formData.get('file')) {
          mutation.mutate(formData);
        }
      } else {
        AlertAdapter(t('global.form.fileRequired'), 'error');
      }
    },
  });

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      formik.setFieldValue('file', event.target?.files[0]);
    }
  };

  return (
    <div>
      {!isUploading ? (
        <>
          <YearEndForm onSubmit={formik.handleSubmit}>
            <div>
              <CommonInput
                label="Year"
                id="year"
                name="year"
                type="year"
                style={{ width: '100%' }}
                onChange={formik.handleChange}
                value={formik.values.year}
              />
            </div>
            <FormErrorWrap visible errorMessage={formik.errors.year || ''} />
            <div>
              <label id="file" htmlFor="file">
                <input type="file" id="file" name="file" multiple={false} accept="xlsx, xls" onChange={onFileChange} />
              </label>
            </div>
            <FormErrorWrap visible errorMessage={formik.errors.file || ''} />
            <div>
              <CommonDropdown
                label="Form Type"
                options={dropdownOptions}
                setValue={(value: string) => {
                  setFormType(value);
                }}
              />
            </div>
            <FormErrorWrap visible errorMessage={formik.errors.year || ''} />

            <RegularButton type="submit" disabled={!formik.isValid || mutation.isLoading}>
              {t('global.form.upload')}
            </RegularButton>
          </YearEndForm>
        </>
      ) : (
        <>
          <p style={{ padding: '50px;' }}>{t('yearEndForms.form.upload')}</p>
          <LoadingUpdateComponent onSubmit={onSubmit} />
        </>
      )}
    </div>
  );
};
